import { Spacing } from '@persona-id/design-tokens';
import { partition } from 'lodash/fp';
import { Fragment } from 'react';
import { FieldArray, FormSection, WrappedFieldArrayProps } from 'redux-form';
import styled from 'styled-components';

import useIsEditableWithOptions, {
  EditorAccessTier,
} from 'dashboard/apps/InquiryTemplateEditor/hooks/useIsEditableWithOptions';
import { DEFAULT_DOCUMENT_ALLOWED_IMAGES } from 'dashboard/lib/stubs/verification-template-version';
import { Label, Paragraph } from 'design-system/components/Text';
import ToggleSlider from 'design-system/components/ToggleSlider';
import { FileType } from 'lib/file';
import { isRequired, withMessage } from 'lib/utils/formValidation';

const ToggleContainer = styled('div')`
  display: flex;
  width: 100%;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  padding: ${Spacing[4]} 0;

  // begin rendering border after initial toggle row
  :not(:first-of-type) {
    border-top: 1px solid;
    border-color: ${({ theme }) => theme.color.border.secondary};
  }
`;

const StyledDescription = styled(Paragraph).attrs(() => ({ size: 'md' }))`
  color: ${({ theme }) => theme.color.text.tertiary};
`;

const ErrorMessage = styled('div')`
  color: ${({ theme }) => theme.color.variants.danger.default};
  font-size: ${({ theme }) => theme.text.paragraph.mdBold.fontSize};
`;

interface FormSectionEnabledFileTypesConfigProps {
  name: string;
  removeCaptureFromCaptureConfig: () => void;
}

const FormSectionEnabledFileTypesConfig = ({
  name,
  removeCaptureFromCaptureConfig,
}: FormSectionEnabledFileTypesConfigProps) => {
  const editableForFreeTier = useIsEditableWithOptions({
    accessTier: EditorAccessTier.InquiriesFree,
  });
  return (
    <FormSection name={name} component={Fragment}>
      <FieldArray
        name='enabled-file-types'
        component={ToggleEnabledFiles}
        disabled={!editableForFreeTier}
        validate={[withMessage('At least one file type is required', isRequired)]}
        removeCaptureFromCaptureConfig={removeCaptureFromCaptureConfig}
      />
    </FormSection>
  );
};

interface ToggleEnabledFilesProps {
  disabled: boolean;
  removeCaptureFromCaptureConfig: () => void;
}

const ToggleEnabledFiles = ({
  fields,
  meta,
  disabled,
  removeCaptureFromCaptureConfig,
}: WrappedFieldArrayProps & ToggleEnabledFilesProps) => {
  const enabledFileTypes = fields.getAll();
  const [imagesInEnabledFileTypes, imagesNotInEnabledFileTypes] = partition(
    (f) => enabledFileTypes.includes(f),
    DEFAULT_DOCUMENT_ALLOWED_IMAGES,
  );
  const addMissingImageTypes = () => {
    imagesNotInEnabledFileTypes.forEach((imageType) => {
      fields.push(imageType);
    });
  };
  const removeExistingImageTypes = () => {
    imagesInEnabledFileTypes
      .map((imageType) => {
        return enabledFileTypes.findIndex((e) => e === imageType);
      })
      // redux form does not have an interface to remove multiple elements from a field
      // If you just iterate over an array of indexes, and call removeField or manipulate it in some other way,
      // at each iteration the index for each of the values changes as you call remove fields
      // To get around this is to first sort your indexes so that each iteration of remove is eliminating
      // the furthest back item thus avoiding the next iteration from being out of position.
      .sort((a, b) => b - a)
      .forEach((index) => {
        fields.remove(index);
      });
  };

  return (
    <>
      <ToggleContainer>
        <div>
          <Label size='md' weight='bold'>
            PDF
          </Label>
          <StyledDescription>Accept PDF submissions</StyledDescription>
        </div>
        <ToggleSlider
          labelOff='Off'
          labelOn='On'
          value={enabledFileTypes.includes(FileType.ApplicationPdf)}
          disabled={disabled}
          onChange={(enabled) => {
            if (enabled) {
              fields.push(FileType.ApplicationPdf);
            } else {
              const index = enabledFileTypes.findIndex((e) => e === FileType.ApplicationPdf);
              if (index >= 0) {
                fields.remove(index);
              }
            }
          }}
        />
      </ToggleContainer>
      <ToggleContainer>
        <div>
          <Label size='md' weight='bold'>
            Images (JPG, PNG, HEIC, TIFF)
          </Label>
          <StyledDescription>Accept image submissions in the above formats</StyledDescription>
        </div>
        <ToggleSlider
          labelOff='Off'
          labelOn='On'
          value={!!imagesInEnabledFileTypes.length}
          disabled={disabled}
          onChange={(enabled) => {
            if (enabled) {
              addMissingImageTypes();
            } else {
              removeExistingImageTypes();
              removeCaptureFromCaptureConfig();
            }
          }}
        />
      </ToggleContainer>
      {meta.invalid && <ErrorMessage>{meta.error}</ErrorMessage>}
    </>
  );
};

export default FormSectionEnabledFileTypesConfig;
